import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDealer } from "src/hooks/useDealer";
import MainLayout from "src/layouts/MainLayout";
import ApplicationTable from "src/components/ApplicationTable";
import TotalSalesSnapshotCard from "./TotalSalesSnapshotCard";
import ForecastedSalesSnapshotCard from "./ForecastedSalesSnapshotCard";
import {
  ForecastTypeSelectorRow,
  ForecastRow,
  TableHeaderRow,
  TableTitle,
} from "./DashboardPage.styles";
import { TotalCountBadge } from "src/components/TotalCountBadge";
import { useDealerUserInfo } from "src/context/DealerUserInfoContext";
import { usePageView } from "src/hooks/usePageView";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import Dropdown from "src/components/Dropdown";
import { GhostButton } from "src/components/Buttons";
import type { TotalSalesData } from "./TotalSalesSnapshotCard";
import type { ForecastedSalesItem } from "./ForecastedSalesSnapshotCard";

interface DashboardReportResponse {
  total_sales: TotalSalesData;
  forecasted_sales: ForecastedSalesItem[];
}

export default () => {
  const navigate = useNavigate();
  const { dealerUserInfo } = useDealerUserInfo();
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();
  const { dealer_id } = useDealer();
  const [forecastType, setForecastType] = useState("funding");
  const [totalSalesData, setTotalSalesData] = useState<TotalSalesData>({});
  const [timeRangeType, setTimeRangeType] = useState<"mtd" | "qtd" | "ytd">(
    "mtd",
  );
  const [forecastedSalesData, setForecastedSalesData] = useState<
    ForecastedSalesItem[]
  >([]);
  const [totalApplicationCount, setTotalApplicationCount] = useState(0);
  const location = useLocation();
  const key = new URLSearchParams(location.search).get("refresh");
  usePageView("Dashboard");

  useEffect(() => {
    if (
      (dealerUserInfo?.dealer_id?.length ?? 0) > 0 &&
      dealer_id === "finding-user-dealer"
    ) {
      void navigate(`/${dealerUserInfo.dealer_id}/dashboard`);
    }
  }, [dealerUserInfo]);

  useEffect(() => {
    if (!dealer_id || dealer_id === "finding-user-dealer") return;

    const path = `/dealer-portal/v1/${dealer_id}/dashboard/report?target_date_type=${forecastType}&total_sales_period_type=${timeRangeType}`;
    void safeApiCall(() => fetchFromApi(path)).then((data) => {
      if (data) {
        const report = data as DashboardReportResponse;
        setTotalSalesData(report.total_sales);
        setForecastedSalesData(report.forecasted_sales);
      }
    });
  }, [dealer_id, forecastType, timeRangeType]);

  return (
    <MainLayout page="dashboard" key={key}>
      <ForecastTypeSelectorRow>
        <Dropdown
          title="Base forecast on:"
          menuItemsObj={{
            funding: "Target funding date",
            delivery: "Target delivery date",
          }}
          selectedItem={forecastType}
          onSelectedItemChange={setForecastType}
        />
      </ForecastTypeSelectorRow>
      <ForecastRow>
        <TotalSalesSnapshotCard
          data={totalSalesData}
          timeRangeType={timeRangeType}
          setTimeRangeType={setTimeRangeType}
        />
        <ForecastedSalesSnapshotCard data={forecastedSalesData} />
      </ForecastRow>
      <TableHeaderRow>
        <TableTitle>
          Updated applications
          <TotalCountBadge>{totalApplicationCount}</TotalCountBadge>
        </TableTitle>
        <GhostButton
          onClick={() => {
            void navigate(`/${dealer_id}/applications`);
          }}
        >
          View all →
        </GhostButton>
      </TableHeaderRow>
      <ApplicationTable
        key={dealer_id}
        allowPagination={false}
        allowFilters={false}
        updateTotalApplicationCount={setTotalApplicationCount}
      />
    </MainLayout>
  );
};
